import * as React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { Badge, Typography } from '@material-ui/core';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { fetchFirstActiveConnectionV302ForFlagCheck } from '@pec/aion-ui-core/slices/activeConnections/activeConnectionsSlice';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';
import { IOrganizationProfileStatus } from '@pec/aion-ui-core/interfaces/organizationProfileNotifications';
import { isProfileCompleted, RequiredStatusesType, SectionStatus } from '@pec/aion-ui-core/helpers/getStatus';
import { NavLinkListItem } from './NavLinkListItem';
import { PageLink } from '../../types/pageLink';
import { updateOrganizationProfileNotifications } from '@pec/aion-ui-core/actions/changeOrganizationProfileNotifications';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const { AionOrganizationProfileUI } = AppName;
const { REACT_APP_NAME } = process.env;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      padding: theme.spacing(1, 2, 1, 7),
      fontSize: 10,
      lineHeight: '29px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '1.5px'
    },
    list: {
      '& > a': {
        paddingLeft: theme.spacing(8)
      },
      '& > a.active .MuiTypography-root': {
        color: 'var(--dark-blue)',
        fontWeight: 500
      }
    },
    dot: {
      top: 4,
      right: -7
    },
    separateLink: {
      '& > a': {
        paddingLeft: theme.spacing(6)
      }
    }
  })
);

type PageLinkExtended = PageLink & {
  activity?: ActivityResourceName;
};

type Props = {
  organizationId: string;
  organizationProfileNotifications?: IOrganizationProfileStatus;
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasManageHiringClientsConnectionsFeature: boolean;
  statuses: RequiredStatusesType;
};
export const OrganizationProfileSection: React.FC<Props> = ({
  organizationId,
  organizationProfileNotifications,
  hasGlobalPermission,
  hasOrganizationPermission,
  hasManageHiringClientsConnectionsFeature,
  statuses
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { areAllServiceGroupsCompleted } = useSelector((state: CommonRootState) => state.activeConnections);

  React.useEffect(() => {
    organizationId && dispatch(fetchFirstActiveConnectionV302ForFlagCheck(organizationId));
  }, [dispatch, organizationId]);

  React.useEffect(() => {
    organizationId && dispatch(fetchOrganizationInfo(organizationId));
  }, [dispatch, organizationId]);

  React.useEffect(() => {
    dispatch(updateOrganizationProfileNotifications(organizationId));
  }, [dispatch, organizationId]);

  const profilePage = {
    id: 'profile',
    notificationKeys: ['contactInformationSection', 'operationDetailsSection'],
    path: '/profile',
    label: t('components.organizationProfile.title', 'Company Profile')
  };
  const myConnectionsPage = {
    id: 'hiringClientSection',
    path: '/my-connections',
    label: t('components.organizationProfile.pages.myConnections', 'My Connections'),
    activity: ActivityResourceName.Releases
  };

  const renderLink = (page: PageLinkExtended) => {
    const isMyConnectionsPage = page.id === 'hiringClientSection';
    let profileNotification = false;

    if (organizationProfileNotifications) {
      const pageStatuses = (page.notificationKeys || [page.id]).reduce((acc, key) => {
        acc.push(...(statuses[key] || []));
        return acc;
      }, [] as SectionStatus[]);

      profileNotification = !isProfileCompleted(pageStatuses);
    }

    const serviceGroupNotification = isMyConnectionsPage && !areAllServiceGroupsCompleted;
    const showNotification = profileNotification || serviceGroupNotification;

    const missingServicesTagForPendo =
      isMyConnectionsPage && !areAllServiceGroupsCompleted ? { id: 'servicesMissingForClients' } : {};

    if (
      page.activity &&
      !(
        hasOrganizationPermission(ActivityAction.Read, page.activity) ||
        hasGlobalPermission(ActivityAction.Read, page.activity)
      )
    ) {
      return null;
    }
    if (REACT_APP_NAME === AionOrganizationProfileUI) {
      return (
        <NavLinkListItem exact to={`/${organizationId}${page.path}`} {...missingServicesTagForPendo}>
          <Badge invisible={!showNotification} variant="dot" color="error" classes={{ dot: classes.dot }}>
            <Typography variant="inherit">{page.label}</Typography>
          </Badge>
        </NavLinkListItem>
      );
    }
    return (
      <AnchorListItem href={`/organizations/${organizationId}${page.path}`} {...missingServicesTagForPendo}>
        <Badge invisible={!showNotification} variant="dot" color="error" classes={{ dot: classes.dot }}>
          <Typography variant="inherit">{page.label}</Typography>
        </Badge>
      </AnchorListItem>
    );
  };

  return (
    <>
      <div className={classes.separateLink}>{renderLink(profilePage)}</div>
      {hasManageHiringClientsConnectionsFeature && (
        <div className={classes.separateLink}>{renderLink(myConnectionsPage)}</div>
      )}
    </>
  );
};
